import { Routes, Route, Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { FolderOpen, FileText, DollarSign, Download, ExternalLink } from "lucide-react";

function DashboardHome() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error("Erro ao carregar projetos");
    } finally {
      setLoading(false);
    }
  };

  const generateCadernoDeObra = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/caderno-de-obra`);
      toast.success("Caderno de Obra gerado com sucesso!");
      console.log("Caderno de Obra:", response.data);
    } catch (error) {
      toast.error("Erro ao gerar Caderno de Obra");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    completed: "bg-blue-100 text-blue-700",
    on_hold: "bg-amber-100 text-amber-700"
  };

  const statusLabels = {
    active: "Em Andamento",
    completed: "Concluído",
    on_hold: "Pausado"
  };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Meus Projetos</h1>
      </div>

      {projects.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Você ainda não possui projetos associados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading text-luvcorp-navy">{project.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{project.location}</p>
                  </div>
                  <Badge className={statusColors[project.status] || "bg-slate-100"}>
                    {statusLabels[project.status] || project.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">{project.description}</p>
                
                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Progresso estimado</span>
                    <span className="font-medium text-luvcorp-navy">60%</span>
                  </div>
                  <Progress value={60} className="h-2" />
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Início</p>
                    <p className="font-medium">{new Date(project.start_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Previsão de Término</p>
                    <p className="font-medium">{new Date(project.estimated_end_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>

                {/* Financial */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4 text-luvcorp-gold" />
                    <span className="text-sm font-medium text-slate-700">Resumo Financeiro</span>
                  </div>
                  <p className="text-2xl font-bold text-luvcorp-navy">
                    R$ {project.contract_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Valor total do contrato</p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => generateCadernoDeObra(project.id)}
                    className="flex-1 bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white"
                    data-testid={`caderno-btn-${project.id}`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Caderno de Obra
                  </Button>
                  <Link to={`/projects/${project.id}`} className="flex-1">
                    <Button variant="outline" className="w-full">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ver Detalhes
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClientDashboard() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<DashboardHome />} />
      </Routes>
    </MainLayout>
  );
}
